import { FC, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Plus, FolderOpen, FileJson, Trash2, Calendar, Calculator, Settings, LayoutGrid, RefreshCcw } from 'lucide-react';
import { Logo } from '@/shared/ui/Logo';
import { motion, AnimatePresence } from 'framer-motion';
import { useProjectLibrary } from '@/entities/estimate';
import { useRooms } from '@/entities/room';
import { useToastStore } from '@/stores';
import { LanguageSelector } from '@/shared/ui/LanguageSelector';
import { SessionWidget } from '@/features/auth';
import { TrialBanner } from '@/features/billing';
import { ConfirmationModal } from '@/shared/ui/ConfirmationModal';
import { roundTo } from '@/shared/lib/number';
import { Skeleton } from '@/shared/ui/Skeleton';
import { ProjectSnapshot } from '@/shared/model';

const StartPage: FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const addToast = useToastStore((s) => s.addToast);
    const { library, deleteProject, addProject } = useProjectLibrary();
    const { rooms, loadProjectSnapshot, resetRooms, createProjectSnapshot } = useRooms();
    
    const [projectIdToDelete, setProjectIdToDelete] = useState<string | number | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(true);

    useEffect(() => {
        const timer = setTimeout(() => {
            setIsLoading(false);
        }, 600);
        return () => clearTimeout(timer);
    }, []);

    const hasActiveProject = rooms.length > 0 && (rooms.length > 1 || (rooms[0].items.length > 0 || rooms[0].dims.length > 0));

    const handleCreateNew = () => {
        resetRooms();
        
        // Register the project in library to enable history/autosave immediately
        const newId = Date.now();
        const now = new Date();
        const tempName = t('projects.default_name', { date: now.toLocaleDateString() });
        
        const initialSnapshot = createProjectSnapshot(tempName);
        initialSnapshot.id = newId;
        
        addProject(initialSnapshot);
        loadProjectSnapshot(initialSnapshot);
        
        navigate('/editor');
        addToast(t('start.messages.new_project_created', 'Создан новый проект'), 'success');
    };

    const handleContinue = () => {
        navigate('/editor');
    };

    const handleOpenProject = (project: ProjectSnapshot) => {
        loadProjectSnapshot(project);
        navigate('/editor');
    };

    const handleImportProject = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (ev) => {
            try {
                const data = JSON.parse(ev.target?.result as string);
                if (data.rooms) {
                    // It's a single project snapshot
                    loadProjectSnapshot(data);
                    navigate('/editor');
addToast(t('start.messages.import_success', 'Проект успешно импортирован'), 'success');
                } else if (data.activeProject) {
                    loadProjectSnapshot(data.activeProject);
                    navigate('/editor');
                    addToast(t('start.messages.import_success', 'Проект извлечен из бэкапа'), 'success');
                }
            } catch {
                addToast(t('start.errors.import_failed', 'Ошибка при чтении файла'), 'error');
            }
        };
        reader.readAsText(file);
    };

    const confirmDelete = () => {
        if (projectIdToDelete) {
            deleteProject(projectIdToDelete);
            setProjectIdToDelete(null);
            addToast(t('start.messages.deleted', 'Проект удален'), 'success');
        }
    };

    return (
        <div className="min-h-screen bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-slate-100 font-sans transition-colors duration-300 overflow-x-hidden">
            {/* Background Decorations */}
            <div className="fixed inset-0 pointer-events-none overflow-hidden">
                <div className="absolute -top-24 -left-24 w-96 h-96 bg-blue-100/50 dark:bg-blue-900/10 rounded-full blur-3xl opacity-50"></div>
                <div className="absolute top-1/2 -right-24 w-80 h-80 bg-purple-100/50 dark:bg-purple-900/10 rounded-full blur-3xl opacity-50"></div>
            </div>

            <header className="relative h-20 flex items-center justify-between px-4 md:px-12 z-30">
                <Logo className="h-9 w-auto shrink-0" />
                <div className="flex items-center gap-2 md:gap-4">
                    <TrialBanner />
                    <SessionWidget />
                    <LanguageSelector />
                    <button 
                        onClick={() => navigate('/dashboard')}
                        className="p-2.5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl hover:bg-slate-50 dark:hover:bg-slate-800 transition-all text-slate-500 hover:text-blue-600"
                        title={t('start.settings', 'Настройки')}
                    >
                        <Settings size={20} />
                    </button>
                </div>
            </header>

            <main className="relative z-10 max-w-6xl mx-auto px-6 pt-12 pb-24">
                {/* Active Project Highlight */}
                <AnimatePresence>
                    {hasActiveProject && (
                        <motion.div 
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="mb-12 bg-white dark:bg-slate-900 rounded-3xl p-6 border-2 border-blue-500 shadow-xl shadow-blue-500/10"
                        >
                            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                                <div className="flex items-center gap-4">
                                    <div className="w-14 h-14 bg-blue-100 dark:bg-blue-900/30 text-blue-600 rounded-2xl flex items-center justify-center animate-pulse">
                                        <RefreshCcw size={28} />
                                    </div>
                                    <div>
                                        <h3 className="text-xs font-semibold text-blue-500 uppercase tracking-wider mb-1">{t('start.active_project_label', 'Текущий черновик')}</h3>
                                        <h2 className="text-xl font-bold">{t('start.continue_working', 'Продолжить работу')}</h2>
                                    </div>
                                </div>
                                <div className="flex items-center gap-3 w-full md:w-auto">
                                    <button 
                                        onClick={handleContinue}
                                        className="flex-1 md:flex-none px-8 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-2xl font-bold text-sm shadow-lg shadow-blue-200 dark:shadow-none transition-all"
                                    >
                                        {t('start.continue_btn', 'ВОЙТИ В РЕДАКТОР')}
                                    </button>
                                </div>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* Hero Actions */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-20">
                    <motion.button
                        whileHover={{ scale: 1.02, y: -5 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={handleCreateNew}
                        className="group relative h-48 bg-gradient-to-br from-blue-600 to-indigo-700 rounded-3xl p-8 text-left text-white shadow-xl shadow-blue-200 dark:shadow-none overflow-hidden"
                    >
                        <div className="relative z-10 flex flex-col h-full justify-between">
                            <div className="bg-white/20 w-12 h-12 rounded-2xl flex items-center justify-center backdrop-blur-md">
                                <Plus size={32} />
                            </div>
                            <div>
                                <h2 className="text-2xl font-bold mb-1">{t('start.new_project', 'Новый проект')}</h2>
                                <p className="text-blue-100 text-sm font-medium opacity-80">{t('start.new_project_desc', 'Начать расчет с чистого листа')}</p>
                            </div>
                        </div>
                        <Plus className="absolute -right-8 -bottom-8 w-48 h-48 opacity-10 group-hover:rotate-12 transition-transform duration-500" />
                    </motion.button>

                    <label className="cursor-pointer">
                        <input type="file" className="hidden" accept=".json" onChange={handleImportProject} />
                        <motion.div
                            whileHover={{ scale: 1.02, y: -5 }}
                            whileTap={{ scale: 0.98 }}
                            className="group relative h-48 bg-white dark:bg-slate-900 border-2 border-dashed border-slate-200 dark:border-slate-800 rounded-3xl p-8 text-left shadow-sm hover:border-blue-400 dark:hover:border-blue-700 transition-all overflow-hidden"
                        >
                            <div className="relative z-10 flex flex-col h-full justify-between">
                                <div className="bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 w-12 h-12 rounded-2xl flex items-center justify-center">
                                    <FolderOpen size={30} />
                                </div>
                                <div>
                                    <h2 className="text-2xl font-bold mb-1">{t('start.open_file', 'Открыть файл')}</h2>
                                    <p className="text-slate-400 dark:text-slate-500 text-sm font-medium">{t('start.open_file_desc', 'Загрузить .json проект с диска')}</p>
                                </div>
                            </div>
                            <FileJson className="absolute -right-8 -bottom-8 w-48 h-48 text-slate-100 dark:text-slate-800/30 opacity-50 group-hover:-rotate-12 transition-transform duration-500" />
                        </motion.div>
                    </label>
                </div>

                {/* Recent Projects List */}
                <div className="space-y-6">
                    <div className="flex items-center justify-between px-2">
                        <h3 className="text-xl font-bold flex items-center gap-3">
                            <LayoutGrid className="text-blue-500" size={22} />
                            {t('start.recent_projects', 'Последние проекты')}
                        </h3>
                        {library.length > 0 && (
                            <span className="text-xs font-bold text-slate-400 uppercase tracking-widest">{library.length} {t('start.projects_count', 'всего')}</span>
                        )}
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                        <AnimatePresence mode="popLayout">
                            {isLoading ? (
                                Array.from({ length: 3 }).map((_, i) => (
                                    <div
                                        key={`skeleton-${i}`}
                                        className="bg-white dark:bg-slate-900 p-5 rounded-3xl border border-slate-100 dark:border-slate-800 space-y-4"
                                    >
                                        <div className="flex justify-between items-start">
                                            <Skeleton className="w-10 h-10 rounded-xl" />
                                            <Skeleton className="w-8 h-8 rounded-lg" />
                                        </div>
                                        <Skeleton className="h-6 w-3/4" />
                                        <div className="flex gap-4">
                                            <Skeleton className="h-4 w-20" />
                                            <Skeleton className="h-4 w-16" />
                                        </div>
                                        <Skeleton className="h-10 w-full rounded-2xl" />
                                    </div>
                                ))
                            ) : library.length === 0 ? (
                                <motion.div 
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    className="col-span-full py-20 bg-white/50 dark:bg-slate-900/30 rounded-3xl border border-slate-100 dark:border-slate-800 flex flex-col items-center justify-center text-center px-4"
                                >
                                    <div className="w-16 h-16 bg-white dark:bg-slate-800 rounded-2xl flex items-center justify-center shadow-sm mb-4 text-slate-300 dark:text-slate-700">
                                        <FolderOpen size={32} />
                                    </div>
                                    <h4 className="text-lg font-bold text-slate-700 dark:text-slate-300">{t('start.empty_library', 'Библиотека пуста')}</h4>
                                    <p className="text-slate-400 dark:text-slate-600 max-w-xs mt-2">{t('start.empty_library_desc', 'Сохраненные проекты появятся здесь для быстрого доступа.')}</p>
                                </motion.div>
                            ) : (
                                [...library].sort((a, b) => new Date(b.lastModified).getTime() - new Date(a.lastModified).getTime()).map((project) => (
                                    <motion.div
                                        key={project.id}
                                        layout
                                        initial={{ opacity: 0, scale: 0.9 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        exit={{ opacity: 0, scale: 0.9 }}
                                        className="group bg-white dark:bg-slate-900 p-5 rounded-3xl border border-slate-100 dark:border-slate-800 hover:border-blue-200 dark:hover:border-blue-900 hover:shadow-xl hover:shadow-blue-500/5 transition-all"
                                    >
                                        <div className="flex justify-between items-start mb-4">
                                            <div className="w-10 h-10 bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 rounded-xl flex items-center justify-center group-hover:bg-blue-50 dark:group-hover:bg-blue-900/20 group-hover:text-blue-600 transition-colors">
                                                <FileJson size={20} />
                                            </div>
                                            <button 
                                                onClick={() => setProjectIdToDelete(project.id)}
                                                className="p-2 text-slate-300 dark:text-slate-600 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-all"
                                            >
                                                <Trash2 size={16} />
                                            </button>
                                        </div>

                                        <h4 className="font-bold text-lg mb-1 truncate">{project.name || t('start.untitled_project', 'Проект без названия')}</h4>
                                        
                                        <div className="flex gap-4 text-[10px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500 mb-6">
                                            <span className="flex items-center gap-1.5"><Calendar size={12}/> <span className="font-mono tabular-nums">{new Date(project.lastModified).toLocaleDateString()}</span></span>
                                            <span className="flex items-center gap-1.5"><Calculator size={12}/> <span className="font-mono tabular-nums">{roundTo(project.stats?.total || 0, 0)}</span></span>
                                        </div>

                                        <button 
                                            onClick={() => handleOpenProject(project)}
                                            className="w-full py-3 bg-slate-50 dark:bg-slate-800 hover:bg-blue-600 text-slate-600 dark:text-slate-300 hover:text-white rounded-2xl font-bold text-xs transition-all flex items-center justify-center gap-2"
                                        >
                                            {t('start.open_project_btn', 'ОТКРЫТЬ')}
                                        </button>
                                    </motion.div>
                                ))
                            )}
                        </AnimatePresence>
                    </div>
                </div>
            </main>

            <ConfirmationModal 
                isOpen={projectIdToDelete !== null}
                onClose={() => setProjectIdToDelete(null)}
                onConfirm={confirmDelete}
                title={t('start.modals.delete_title', 'Удалить проект?')}
                message={t('start.modals.delete_message', 'Это действие нельзя отменить. Все данные проекта в памяти браузера будут стерты.')}
            />
        </div>
    );
};

export default StartPage;
